// ─────────────────────────────────────────────────────────────
// TypesView — CRUD for song types with visual Ionicons picker
// ─────────────────────────────────────────────────────────────

import {createElement} from "@opendaw/lib-jsx"
import {Lifecycle} from "@opendaw/lib-std"
import {STApi} from "../STApi"
import {STType} from "../STTypes"
import {confirmDelete} from "./ConfirmDeleteDialog"

const DEFAULT_COLORS = [
    "#7c5cbf", "#5cb8bf", "#bf5c7c", "#80c080",
    "#c0b060", "#6090c0", "#c08050", "#9060c0",
]

// ─── Full Ionicons v7 name list ───────────────────────────────
const ALL_ICONS: string[] = [
    // Music & Audio
    "musical-notes","musical-notes-outline","musical-note","musical-note-outline",
    "mic","mic-outline","mic-off","mic-off-outline",
    "headset","headset-outline","radio","radio-outline",
    "disc","disc-outline","volume-high","volume-high-outline",
    "volume-medium","volume-medium-outline","volume-low","volume-low-outline",
    "volume-mute","volume-mute-outline","volume-off","volume-off-outline",
    "ear","ear-outline",
    // Energy & Worship
    "flash","flash-outline","flash-off","flash-off-outline",
    "flame","flame-outline","sparkles","sparkles-outline",
    "sunny","sunny-outline","star","star-outline",
    "star-half","star-half-outline","trophy","trophy-outline",
    "ribbon","ribbon-outline","medal","medal-outline",
    "heart","heart-outline","heart-half","heart-half-outline",
    "rose","rose-outline","flower","flower-outline",
    // People
    "person","person-outline","people","people-outline",
    "person-add","person-add-outline","person-remove","person-remove-outline",
    "body","body-outline","man","man-outline","woman","woman-outline",
    "hand-right","hand-right-outline","hand-left","hand-left-outline",
    "thumbs-up","thumbs-up-outline","thumbs-down","thumbs-down-outline",
    // Playback & Structure
    "play","play-outline","pause","pause-outline",
    "stop","stop-outline","repeat","repeat-outline",
    "shuffle","shuffle-outline","skip-forward","skip-forward-outline",
    "skip-back","skip-back-outline","play-back","play-back-outline",
    "play-forward","play-forward-outline","play-circle","play-circle-outline",
    "pause-circle","pause-circle-outline","stop-circle","stop-circle-outline",
    "infinite","infinite-outline","reload","reload-outline",
    // Communication
    "megaphone","megaphone-outline","chatbubble","chatbubble-outline",
    "chatbubbles","chatbubbles-outline","mail","mail-outline",
    "send","send-outline","notifications","notifications-outline",
    "notifications-off","notifications-off-outline","alert","alert-outline",
    "alert-circle","alert-circle-outline","information","information-outline",
    "information-circle","information-circle-outline",
    // Navigation & UI
    "home","home-outline","menu","menu-outline",
    "grid","grid-outline","list","list-outline",
    "apps","apps-outline","layers","layers-outline",
    "albums","albums-outline","library","library-outline",
    "reorder-two","reorder-two-outline","reorder-three","reorder-three-outline",
    "filter","filter-outline","funnel","funnel-outline",
    "search","search-outline","options","options-outline",
    "settings","settings-outline","settings-sharp",
    "add","add-outline","add-circle","add-circle-outline",
    "remove","remove-outline","remove-circle","remove-circle-outline",
    "close","close-outline","close-circle","close-circle-outline",
    "checkmark","checkmark-outline","checkmark-circle","checkmark-circle-outline",
    "chevron-up","chevron-down","chevron-forward","chevron-back",
    "arrow-up","arrow-down","arrow-forward","arrow-back",
    "refresh","refresh-outline","sync","sync-outline",
    // Time & Calendar
    "time","time-outline","calendar","calendar-outline",
    "calendar-clear","calendar-clear-outline","alarm","alarm-outline",
    "stopwatch","stopwatch-outline","hourglass","hourglass-outline",
    "timer","timer-outline",
    // Files & Data
    "document","document-outline","documents","documents-outline",
    "document-text","document-text-outline","folder","folder-outline",
    "folder-open","folder-open-outline","archive","archive-outline",
    "cloud","cloud-outline","cloud-upload","cloud-upload-outline",
    "cloud-download","cloud-download-outline","save","save-outline",
    "copy","copy-outline","clipboard","clipboard-outline",
    "attach","attach-outline","link","link-outline",
    "download","download-outline","share","share-outline",
    // Devices & Tech
    "phone-portrait","phone-portrait-outline","tablet-portrait","tablet-portrait-outline",
    "laptop","laptop-outline","tv","tv-outline","desktop","desktop-outline",
    "wifi","wifi-outline","bluetooth","bluetooth-outline",
    "battery-full","battery-full-outline","battery-half","battery-half-outline",
    "power","power-outline","key","key-outline",
    "lock-closed","lock-closed-outline","lock-open","lock-open-outline",
    // Nature & Weather
    "leaf","leaf-outline","earth","earth-outline",
    "water","water-outline","snow","snow-outline",
    "thunderstorm","thunderstorm-outline","cloudy","cloudy-outline",
    "partly-sunny","partly-sunny-outline","moon","moon-outline",
    "rainy","rainy-outline","umbrella","umbrella-outline",
    // Misc
    "flag","flag-outline","bookmark","bookmark-outline",
    "pricetag","pricetag-outline","pricetags","pricetags-outline",
    "gift","gift-outline","bag","bag-outline","cart","cart-outline",
    "cash","cash-outline","card","card-outline","wallet","wallet-outline",
    "diamond","diamond-outline","cube","cube-outline","shapes","shapes-outline",
    "color-palette","color-palette-outline","brush","brush-outline",
    "pencil","pencil-outline","create","create-outline",
    "cut","cut-outline","scissors","scissors-outline",
    "resize","resize-outline","move","contract","contract-outline",
    "expand","expand-outline","eye","eye-outline","eye-off","eye-off-outline",
    "image","image-outline","images","images-outline","camera","camera-outline",
    "videocam","videocam-outline","film","film-outline",
    "map","map-outline","navigate","navigate-outline","compass","compass-outline",
    "location","location-outline","pin","pin-outline",
    "bicycle","bicycle-outline","car","car-outline","bus","bus-outline",
    "airplane","airplane-outline","rocket","rocket-outline",
    "barbell","barbell-outline","football","football-outline","basketball","basketball-outline",
    "pizza","pizza-outline","fast-food","fast-food-outline","cafe","cafe-outline",
    "beer","beer-outline","wine","wine-outline",
    "medkit","medkit-outline","bandage","bandage-outline","pulse","pulse-outline",
    "fitness","fitness-outline","walk","walk-outline","accessibility","accessibility-outline",
    "school","school-outline","business","business-outline","storefront","storefront-outline",
    "construct","construct-outline","hammer","hammer-outline","build","build-outline",
    "bug","bug-outline","code","code-outline","terminal","terminal-outline",
    "git-branch","git-branch-outline","git-commit","git-commit-outline",
]

// ─── ion-icon helper (avoids JSX — custom element not in IntrinsicElements) ───
function ionIcon(name: string, style?: Partial<CSSStyleDeclaration>): HTMLElement {
    const el = document.createElement("ion-icon") as HTMLElement
    el.setAttribute("name", name)
    if (style) Object.assign(el.style, style)
    return el
}

export const TypesView = ({}: {lifecycle: Lifecycle}) => {
    let types: STType[] = STApi.getTypes()
    let editingId: string | null = null
    let selectedIcon: string | undefined = undefined
    let pickerVisible = false
    let filteredIcons: string[] = ALL_ICONS
    let usageCounts: Record<string, number> = {}

    // ── Form inputs ───────────────────────────────────────────
    const nameInput: HTMLInputElement = (
        <input className="st-input" type="text" placeholder="Type name…" style={{flex:"1"}}/>
    )
    const colorInput: HTMLInputElement = (
        <input type="color" value="#7c5cbf" style={{
            width:"28px", height:"28px", padding:"2px",
            background:"none", border:"1px solid rgba(255,255,255,0.1)",
            borderRadius:"4px", cursor:"pointer"
        }}/>
    )
    const filterCheckbox: HTMLInputElement = (
        <input type="checkbox" id="showInFilterCheck" style={{
            width:"14px", height:"14px", cursor:"pointer", accentColor:"#5cb8bf"
        }}/>
    )

    // ── Icon trigger button ───────────────────────────────────
    const iconTriggerIcon: HTMLElement = ionIcon("musical-note-outline")
    const iconTriggerLabel: HTMLElement = (
        <span className="st-icon-trigger-label">Choose icon…</span>
    )
    const iconTrigger: HTMLElement = (
        <div className="st-icon-trigger">
            {iconTriggerIcon}
            {iconTriggerLabel}
        </div>
    )

    // ── Icon picker ───────────────────────────────────────────
    const iconSearchInput: HTMLInputElement = (
        <input className="st-icon-search" type="text" placeholder="Search icons…"/>
    )
    const iconGrid: HTMLElement = <div className="st-icon-grid"/>
    const iconPickerEl: HTMLElement = (
        <div className="st-icon-picker" style={{display:"none"}}>
            {iconSearchInput}
            {iconGrid}
        </div>
    )

    const updateIconTrigger = () => {
        if (selectedIcon) {
            iconTriggerIcon.setAttribute("name", selectedIcon)
            iconTriggerLabel.textContent = selectedIcon
            iconTriggerLabel.classList.add("has-value")
        } else {
            iconTriggerIcon.setAttribute("name", "musical-note-outline")
            iconTriggerLabel.textContent = "Choose icon…"
            iconTriggerLabel.classList.remove("has-value")
        }
    }

    const renderIconGrid = () => {
        iconGrid.innerHTML = ""
        if (filteredIcons.length === 0) {
            iconGrid.appendChild(
                <div className="st-icon-no-results">No icons found</div>
            )
            return
        }
        for (const name of filteredIcons) {
            const cell: HTMLElement = (
                <div className={`st-icon-cell${selectedIcon === name ? " selected" : ""}`} data-icon={name}>
                    {ionIcon(name)}
                    <span className="st-icon-name">{name.replace(/-outline$/, "")}</span>
                </div>
            )
            cell.addEventListener("click", () => {
                selectedIcon = name
                updateIconTrigger()
                renderIconGrid()
            })
            iconGrid.appendChild(cell)
        }
    }

    iconSearchInput.addEventListener("input", () => {
        const q = iconSearchInput.value.trim().toLowerCase()
        filteredIcons = q ? ALL_ICONS.filter(n => n.includes(q)) : ALL_ICONS
        renderIconGrid()
    })

    iconTrigger.addEventListener("click", () => {
        pickerVisible = !pickerVisible
        iconPickerEl.style.display = pickerVisible ? "block" : "none"
        if (pickerVisible) {
            iconSearchInput.value = ""
            filteredIcons = ALL_ICONS
            renderIconGrid()
            iconSearchInput.focus()
        }
    })

    renderIconGrid()

    // ── Usage counts ──────────────────────────────────────────
    // Fetch once on mount; re-render list when counts arrive.
    STApi.getUsageCounts()
        .then(counts => { usageCounts = counts.types; renderList() })
        .catch(() => { /* non-fatal — counts stay 0 */ })

    // ── List ──────────────────────────────────────────────────
    const listEl: HTMLElement = <div/>

    const renderList = () => {
        listEl.innerHTML = ""
        if (types.length === 0) {
            listEl.appendChild(
                <div className="st-empty">No types yet.<br/>Add one below.</div>
            )
            return
        }
        for (const t of types) {
            const filterBadge: HTMLElement = t.showInFilter
                ? <span style={{
                    fontSize:"9px", fontWeight:"700", letterSpacing:"0.06em",
                    padding:"1px 5px", borderRadius:"3px",
                    background:"rgba(92,184,191,0.18)", color:"#5cb8bf",
                    border:"1px solid rgba(92,184,191,0.3)", flexShrink:"0",
                  }}>FILTER</span>
                : <span/>

            const iconEl: HTMLElement = t.iconName
                ? ionIcon(t.iconName, {fontSize:"12px", color:"rgba(255,255,255,0.45)", flexShrink:"0"})
                : <span/>

            const count = usageCounts[t.id] ?? 0
            const countBadge: HTMLElement = (
                <span style={{
                    fontSize:"9px", fontWeight:"600", letterSpacing:"0.04em",
                    padding:"1px 5px", borderRadius:"3px", flexShrink:"0",
                    background:"rgba(255,255,255,0.07)", color:"rgba(255,255,255,0.35)",
                }}>{count} {count === 1 ? "song" : "songs"}</span>
            )

            const item: HTMLElement = (
                <div className="st-crud-item">
                    <div className="st-color-dot" style={{backgroundColor: t.color}}/>
                    {iconEl}
                    <span className="st-crud-name">{t.name}</span>
                    {countBadge}
                    {filterBadge}
                    <div className="st-crud-actions">
                        <div className="st-btn" data-id={t.id} data-action="edit" style={{padding:"0.2rem 0.5rem"}}>Edit</div>
                        <div className="st-btn danger" data-id={t.id} data-action="del" style={{padding:"0.2rem 0.5rem"}}>✕</div>
                    </div>
                </div>
            )
            listEl.appendChild(item)
        }

        listEl.querySelectorAll<HTMLElement>("[data-action='edit']").forEach(btn => {
            btn.addEventListener("click", () => {
                const id = btn.getAttribute("data-id")!
                const t = types.find(x => x.id === id)
                if (!t) return
                editingId = id
                nameInput.value = t.name
                colorInput.value = t.color
                filterCheckbox.checked = t.showInFilter ?? false
                selectedIcon = t.iconName
                updateIconTrigger()
                addBtn.textContent = "Save"
                cancelBtn.style.display = "inline-flex"
                nameInput.focus()
            })
        })

        listEl.querySelectorAll<HTMLElement>("[data-action='del']").forEach(btn => {
            btn.addEventListener("click", async () => {
                const id = btn.getAttribute("data-id")!
                const t = types.find(x => x.id === id)
                if (!t) return
                const confirmed = await confirmDelete({name: t.name, kind: "type"})
                if (!confirmed) return
                await STApi.deleteType(id)
                types = STApi.getTypes()
                if (editingId === id) resetForm()
                renderList()
            })
        })
    }

    // ── Form controls ─────────────────────────────────────────
    const addBtn: HTMLElement = (
        <div className="st-btn primary" style={{flexShrink:"0"}}>Add</div>
    )
    const cancelBtn: HTMLElement = (
        <div className="st-btn" style={{flexShrink:"0", display:"none"}}>Cancel</div>
    )
    const nameError: HTMLElement = (
        <div style={{
            display:"none", fontSize:"0.6rem", color:"var(--color-red)",
            marginTop:"0.25rem", marginBottom:"0.2rem",
        }}>A type with this name already exists.</div>
    )

    const resetForm = () => {
        editingId = null
        nameInput.value = ""
        colorInput.value = "#7c5cbf"
        filterCheckbox.checked = false
        selectedIcon = undefined
        pickerVisible = false
        iconPickerEl.style.display = "none"
        updateIconTrigger()
        addBtn.textContent = "Add"
        cancelBtn.style.display = "none"
    }

    addBtn.addEventListener("click", async () => {
        const name = nameInput.value.trim()
        if (!name) return
        // Duplicate name guard — case-insensitive, exclude self when editing
        const duplicate = STApi.getTypes().find(t =>
            t.name.toLowerCase() === name.toLowerCase() && t.id !== editingId
        )
        if (duplicate) {
            nameError.style.display = "block"
            nameInput.focus()
            return
        }
        nameError.style.display = "none"
        const showInFilter = filterCheckbox.checked
        let savedType: STType
        if (editingId) {
            STApi.updateType(editingId, {name, color: colorInput.value, showInFilter, iconName: selectedIcon})
            savedType = STApi.getTypes().find(t => t.id === editingId)!
        } else {
            savedType = STApi.createType(name, colorInput.value, showInFilter, selectedIcon)
        }
        // Push to D1 immediately — no publish needed for type metadata changes
        try { await STApi.upsertType(savedType) }
        catch (e) { console.warn("[TypesView] upsertType failed:", e) }
        types = STApi.getTypes()
        resetForm()
        renderList()
    })
    nameInput.addEventListener("input", () => { nameError.style.display = "none" })

    cancelBtn.addEventListener("click", resetForm)

    const colorPresets: HTMLElement = (
        <div style={{display:"flex", gap:"0.3rem", flexWrap:"wrap", marginBottom:"0.5rem"}}>
            {DEFAULT_COLORS.map(c => {
                const dot: HTMLElement = (
                    <div style={{
                        width:"16px", height:"16px", borderRadius:"50%",
                        backgroundColor:c, cursor:"pointer",
                        border:"2px solid transparent",
                    }}/>
                )
                dot.addEventListener("click", () => { colorInput.value = c })
                return dot
            })}
        </div>
    )

    renderList()

    return (
        <div>
            {listEl}
            <div className="st-section" style={{borderTop:"1px solid rgba(255,255,255,0.06)"}}>
                <div className="st-label" style={{marginBottom:"0.5rem"}}>Add / Edit Type</div>
                {colorPresets}
                <div className="st-row" style={{marginBottom:"0.1rem"}}>
                    {colorInput}
                    {nameInput}
                </div>
                {nameError}
                <div style={{marginBottom:"0.4rem"}}>
                    {iconTrigger}
                    {iconPickerEl}
                </div>
                <div className="st-row" style={{marginBottom:"0.4rem", alignItems:"center", gap:"0.5rem"}}>
                    {filterCheckbox}
                    <label htmlFor="showInFilterCheck" style={{
                        fontSize:"11px", color:"rgba(255,255,255,0.55)", cursor:"pointer", userSelect:"none"
                    }}>
                        Show as filter pill in app
                    </label>
                </div>
                <div className="st-row">
                    {addBtn}
                    {cancelBtn}
                </div>
            </div>
        </div>
    )
}
